import { useNavigation } from '@react-navigation/native';
import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Image,
  RefreshControl,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';

import { useSocket } from '../hooks/useSocket';
import httpClient from '../utils/httpClient';

interface Contact {
  id: string;
  name: string;
  phone?: string;
  avatar?: string;
  status?: 'online' | 'offline' | 'busy';
  nickname?: string;
  remark?: string;
}

export default function ContactsScreen() {
  const navigation = useNavigation();
  const { on, off } = useSocket();

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [searchText, setSearchText] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadContacts = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      // 从服务器加载联系人列表
      const response = await httpClient.get('/contacts');
      const data: Contact[] = response.data?.contacts || response.data || [];

      // 格式化联系人数据
      const formattedContacts = data.map((contact: any) => ({
        id: contact.id || contact.userId,
        name: contact.remark || contact.nickname || contact.name || '未知',
        phone: contact.phone,
        avatar: contact.avatar,
        status: contact.status || 'offline',
        nickname: contact.nickname,
        remark: contact.remark,
      }));

      setContacts(formattedContacts);
      setFilteredContacts(formattedContacts);
    } catch (err: any) {
      console.error('Failed to load contacts:', err);
      setError(err.message || '加载联系人列表失败');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // 监听联系人状态变化
  useEffect(() => {
    const handleUserStatusChange = (data: {
      userId: string;
      status: 'online' | 'offline' | 'busy';
    }) => {
      setContacts((prev) =>
        prev.map((contact) =>
          contact.id === data.userId ? { ...contact, status: data.status } : contact,
        ),
      );
    };

    const handleContactAdded = (contact: any) => {
      const newContact: Contact = {
        id: contact.id || contact.userId,
        name: contact.remark || contact.nickname || contact.name || '未知',
        phone: contact.phone,
        avatar: contact.avatar,
        status: contact.status || 'offline',
        nickname: contact.nickname,
        remark: contact.remark,
      };
      setContacts((prev) => [...prev, newContact]);
    };

    on('user:status', handleUserStatusChange);
    on('contact:added', handleContactAdded);

    return () => {
      off('user:status', handleUserStatusChange);
      off('contact:added', handleContactAdded);
    };
  }, [on, off]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  useEffect(() => {
    if (searchText) {
      const filtered = contacts.filter(
        (contact) =>
          contact.name.toLowerCase().includes(searchText.toLowerCase()) ||
          contact.phone?.includes(searchText) ||
          contact.nickname?.toLowerCase().includes(searchText.toLowerCase()) ||
          contact.remark?.toLowerCase().includes(searchText.toLowerCase()),
      );
      setFilteredContacts(filtered);
    } else {
      setFilteredContacts(contacts);
    }
  }, [searchText, contacts]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadContacts();
  };

  if (loading && !refreshing) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007AFF" />
        <Text style={styles.loadingText}>加载中...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>{error}</Text>
        <TouchableOpacity style={styles.retryButton} onPress={loadContacts}>
          <Text style={styles.retryText}>重试</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const handlePress = (contact: Contact) => {
    navigation.navigate('Chat' as any, { userId: contact.id });
  };

  const renderContact = ({ item }: { item: Contact }) => {
    return (
      <TouchableOpacity style={styles.contact} onPress={() => handlePress(item)}>
        <View style={styles.avatarContainer}>
          <Image
            source={{ uri: item.avatar || 'https://via.placeholder.com/50' }}
            style={styles.avatar}
          />
          {item.status === 'online' && <View style={[styles.statusDot, styles.statusOnline]} />}
          {item.status === 'busy' && <View style={[styles.statusDot, styles.statusBusy]} />}
        </View>
        <View style={styles.contactInfo}>
          <Text style={styles.name} numberOfLines={1}>
            {item.name}
          </Text>
          {item.phone && (
            <Text style={styles.phone} numberOfLines={1}>
              {item.phone}
            </Text>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  // 按首字母分组
  const groupedContacts = filteredContacts.reduce((groups: any, contact) => {
    const firstLetter = contact.name[0]?.toUpperCase() || '#';
    if (!groups[firstLetter]) {
      groups[firstLetter] = [];
    }
    groups[firstLetter].push(contact);
    return groups;
  }, {});

  const sectionData = Object.keys(groupedContacts)
    .sort()
    .map((letter) => ({
      title: letter,
      data: groupedContacts[letter],
    }));

  const renderSection = (props: { item: { title: string; data: Contact[] } }) => {
    const { item: section } = props;
    return (
      <View>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>{section.title}</Text>
        </View>
        {section.data.map((contact) => renderContact({ item: contact }))}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.searchBar}>
        <TextInput
          style={styles.searchInput}
          placeholder="搜索联系人"
          value={searchText}
          onChangeText={setSearchText}
          placeholderTextColor="#999"
        />
      </View>
      <FlatList
        data={sectionData}
        renderItem={renderSection}
        keyExtractor={(item: any) => item.title}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}
        ListEmptyComponent={
          <View style={styles.empty}>
            <Text style={styles.emptyText}>暂无联系人</Text>
          </View>
        }
      />
    </View>
  );
}

const styles = StyleSheet.create({
  avatar: {
    borderRadius: 22,
    height: 44,
    width: 44,
  },
  avatarContainer: {
    marginRight: 12,
    position: 'relative',
  },
  contact: {
    alignItems: 'center',
    backgroundColor: '#fff',
    borderBottomColor: '#f5f5f5',
    borderBottomWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  contactInfo: {
    flex: 1,
  },
  container: {
    backgroundColor: '#f5f5f5',
    flex: 1,
  },
  empty: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    paddingVertical: 100,
  },
  emptyText: {
    color: '#999',
    fontSize: 16,
  },
  errorContainer: {
    alignItems: 'center',
    backgroundColor: '#fff',
    flex: 1,
    justifyContent: 'center',
    padding: 20,
  },
  errorText: {
    color: '#ff4d4f',
    fontSize: 16,
    marginBottom: 16,
    textAlign: 'center',
  },
  loadingContainer: {
    alignItems: 'center',
    backgroundColor: '#fff',
    flex: 1,
    justifyContent: 'center',
  },
  loadingText: {
    color: '#999',
    fontSize: 16,
    marginTop: 12,
  },
  name: {
    color: '#333',
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  phone: {
    color: '#999',
    fontSize: 14,
  },
  retryButton: {
    backgroundColor: '#007AFF',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  retryText: {
    color: '#fff',
    fontSize: 16,
  },
  searchBar: {
    backgroundColor: '#fff',
    borderBottomColor: '#e5e5e5',
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  searchInput: {
    backgroundColor: '#f5f5f5',
    borderRadius: 18,
    color: '#333',
    fontSize: 15,
    height: 36,
    paddingHorizontal: 16,
  },
  sectionHeader: {
    backgroundColor: '#fff',
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  sectionTitle: {
    color: '#666',
    fontSize: 14,
    fontWeight: 'bold',
  },
  statusBusy: {
    backgroundColor: '#ff4d4f',
  },
  statusDot: {
    borderColor: '#fff',
    borderRadius: 6,
    borderWidth: 2,
    bottom: 2,
    height: 12,
    position: 'absolute',
    right: 2,
    width: 12,
  },
  statusOnline: {
    backgroundColor: '#52c41a',
  },
});
